import React, { useState, useEffect, useContext, useMemo } from "react";

import Card from "@material-ui/core/Card";
import DataCard from "./DataCard";
import DataTitleCard from "./DataTitleCard";
import Grid from "@material-ui/core/Grid";
import moment from "moment";
import "moment-timezone";
import { useApolloClient } from "@apollo/react-hooks";
import { GET_BEST_TIME_SLICE_GEO } from "../GraphQL/GetGeoTimeSliceValues";
import { makeStyles } from "@material-ui/core/styles";

import { ChartDataContext } from "../../context/ChartDataContext";

import "./DataPane.scss";

const useStyles = makeStyles({
  root: {
    background: "RGB(37, 42, 63)",
    cursor: "grab",
    minWidth: "363px",
    minHeight: "86px",
    padding: "0"
  },
  card: {
    background: "RGB(37, 42, 63)",
    cursor: "pointer",
    minWidth: "363px",
    minHeight: "86px",
    padding: "0",
    "&:hover": {
      backgroundColor: "#00081C"
    },
    "&:active": {
      backgroundColor: "#171B26"
    }
  },
  activeCard: {
    background: "#171B26",
    borderLeft: "5px solid #FFC200",
    cursor: "pointer",
    minWidth: "363px",
    minHeight: "86px",
    padding: "0"
  },
  windCard: {
    background: "RGB(37, 42, 63)",
    cursor: "grab",
    minWidth: "363px",
    minHeight: "86px",
    padding: "0"
  }
});

const dataPoints = {
  waveHeight: {
    title: "wave height",
    graph: "wave",
    measurement: "m",
    toFixed: "2"
  },
  significantWaveHeight: {
    title: "wave height",
    graph: "wave",
    measurement: "m",
    toFixed: "2"
  },
  peakPeriod: {
    title: "wave peak period",
    graph: "period",
    measurement: "s",
    toFixed: "2"
  },
  meanPeriod: {
    title: "wave mean period",
    graph: "period",
    measurement: "s",
    toFixed: "2"
  },
  period: {
    title: "wave mean period",
    graph: "period",
    measurement: "s",
    toFixed: "2"
  },
  peakDirection: {
    title: "wave peak direction",
    graph: "direction",
    measurement: "°",
    toFixed: "0"
  },
  peakDirectionalSpread: {
    title: "wave peak spread",
    graph: "spread",
    measurement: "°",
    toFixed: "0"
  },
  meanDirection: {
    title: "wave mean direction",
    graph: "direction",
    measurement: "°",
    toFixed: "0"
  },
  meanDirectionalSpread: {
    title: "wave mean spread",
    graph: "spread",
    measurement: "°",
    toFixed: "0"
  },
  spread: {
    title: "wave mean spread",
    graph: "spread",
    measurement: "°",
    toFixed: "0"
  },
  speed: {
    title: "wind speed",
    measurement: "m/s",
    toFixed: "0"
  },
  windMagnitude: {
    title: "wind speed",
    measurement: "m/s",
    toFixed: "0"
  },
  direction: {
    title: "wind direction",
    measurement: "°",
    toFixed: "0"
  },
  windDirection: {
    title: "wind direction",
    measurement: "°",
    toFixed: "0"
  }
};
const spotterDataPointKeys = [
  "significantWaveHeight",
  "peakPeriod",
  "meanPeriod",
  "peakDirection",
  "meanDirection",
  "peakDirectionalSpread",
  "meanDirectionalSpread"
];
const windDataPointKeys = ["speed", "direction"];
const pointDataPointKeys = [
  "waveHeight",
  "period",
  "peakDirection",
  "meanDirection",
  "spread",
  "windMagnitude",
  "windDirection"
];

const DataPane = ({ spotterData, pointData }) => {
  const classes = useStyles();

  const client = useApolloClient();

  const [batchedData, setBatchedData] = useState(null);
  const { selectedData, setQueriedData, setSelectedData } = useContext(
    ChartDataContext
  );

  const filteredSpotterData = useMemo(() => {
    if (spotterData) {
      return Object.keys(spotterData.waves[0])
        .filter(key => spotterDataPointKeys.includes(key))
        .reduce((obj, key) => {
          obj[key] = spotterData.waves[0][key];
          return obj;
        }, {});
    } else return;
  }, [spotterData]);

  const spotterWindData = useMemo(() => {
    if (spotterData) {
      if (spotterData.wind.length > 0) {
        return Object.keys(spotterData.wind[0])
          .filter(key => ["speed", "direction"].includes(key))
          .reduce((obj, key) => {
            obj[key] = spotterData.wind[0][key];
            return obj;
          }, {});
      }
    }
  }, [spotterData]);

  const handleCardClick = (event, index, graph) => {
    setSelectedData({
      ...selectedData,
      selectedGraph: graph,
      selectedIndex: index
    });
  };

  useEffect(() => {
    if (spotterData) {
      setQueriedData({ batched: null, spotter: filteredSpotterData });
      return;
    }

    let longitudeData = pointData[0];

    if (longitudeData > 0) longitudeData -= 360;

    client
      .query({
        fetchPolicy: "cache-first",
        query: GET_BEST_TIME_SLICE_GEO,
        context: { clientName: "wexGeo" },
        variables: {
          start: moment()
            .startOf("hour")
            .subtract(1, "hour"),
          end: moment().startOf("hour"),
          location: { longitude: pointData[0] + 360, latitude: pointData[1] }
        },
        onError: e => console.error(`use query: ${e}`)
      })
      .then(res => {
        setBatchedData(res.data);
        setQueriedData({ spotter: null, batched: res.data });
      });
  }, [client, filteredSpotterData, pointData, setQueriedData, spotterData]);

  return (
    <div className="datapane-container" id="datapane-container">
      {spotterData ? (
        <React.Fragment>
          <Grid style={{ paddingBottom: "5px" }} container>
            <Grid item>
              <Card className={classes.root}>
                <DataTitleCard
                  spotterName={spotterData.spotterId}
                  spotterTime={spotterData.track[0].timestamp}
                  spotterTrack={[
                    spotterData.track[0].latitude,
                    spotterData.track[0].longitude
                  ]}
                />
              </Card>
            </Grid>
          </Grid>
          <div className="datapane-scrollable">
            {spotterDataPointKeys.map((data, index) => (
              <Grid key={data} style={{ paddingBottom: "5px" }} container>
                <Grid item>
                  <Card
                    onClick={e =>
                      handleCardClick(e, index, dataPoints[data].graph)
                    }
                    className={
                      selectedData.selectedIndex === index
                        ? classes.activeCard
                        : classes.card
                    }
                  >
                    <DataCard
                      data={filteredSpotterData[data]}
                      dataType={dataPoints[data]}
                    />
                  </Card>
                </Grid>
              </Grid>
            ))}
            {spotterWindData
              ? windDataPointKeys.map(data => (
                  <Grid
                    key={data}
                    style={{ cursor: "grab", paddingBottom: "5px" }}
                    container
                  >
                    <Grid item>
                      <Card className={classes.windCard}>
                        <DataCard
                          data={spotterWindData[data]}
                          dataType={dataPoints[data]}
                        />
                      </Card>
                    </Grid>
                  </Grid>
                ))
              : null}
          </div>
        </React.Fragment>
      ) : (
        <React.Fragment>
          {batchedData && (
            <React.Fragment>
              <Grid style={{ paddingBottom: "5px" }} container>
                <Grid item>
                  <Card className={classes.root}>
                    <DataTitleCard
                      spotterName={`${pointData[0].toFixed(
                        2
                      )}, ${pointData[1].toFixed(2)}`}
                      spotterTrack={pointData}
                    />
                  </Card>
                </Grid>
              </Grid>
              <div className="datapane-scrollable">
                {pointDataPointKeys.map((data, index) => (
                  <Grid key={data} style={{ paddingBottom: "5px" }} container>
                    <Grid item>
                      {["windDirection", "windMagnitude"].indexOf(data) ===
                      -1 ? (
                        <Card
                          onClick={e =>
                            handleCardClick(
                              e,
                              index * 2,
                              dataPoints[data].graph
                            )
                          }
                          className={
                            Math.ceil(selectedData.selectedIndex / 2) === index
                              ? classes.activeCard
                              : classes.card
                          }
                        >
                          <DataCard
                            data={
                              batchedData[data].length > 0
                                ? batchedData[data][0].value
                                : null
                            }
                            dataType={dataPoints[data]}
                          />
                        </Card>
                      ) : (
                        <Card className={classes.windCard}>
                          <DataCard
                            data={
                              batchedData[data].length > 0
                                ? batchedData[data][0].value
                                : null
                            }
                            dataType={dataPoints[data]}
                          />
                        </Card>
                      )}
                    </Grid>
                  </Grid>
                ))}
              </div>
            </React.Fragment>
          )}
        </React.Fragment>
      )}
    </div>
  );
};

export default DataPane;
